import { makeAutoObservable } from 'mobx';
import extractValue, {
  type EventOrValue,
} from '../../../store/utils/extractValue';
import { errorHandler } from '../../../utils/errors';
import Labels from './Labels';
import Cdn from './services/Cdn';
import type { ServicesDto } from './services/Services';
import Storages from './services/storages/Storages';
import {
  groupByProvider,
  StorageProvider,
  WebhookProvider,
} from './services/types';
import Webhooks from './services/webhooks/Webhooks';
import type { AccountSaveArgs, AccountSaveMethod } from './types';

export default class Configurations {
  disablePulseKeys = false;
  isWebhookEnabled = false;
  managedStorage = false;
  orderEmailTemplate = '';
  lowSerialThreshold = 50;
  pricePercentagePerKey = 1;
  skipWebhookOrders = false;

  labels: Labels;

  storages: Storages;
  webhooks: Webhooks;
  cdn: Cdn;

  constructor(
    { services, labels, ...dto }: ConfigurationsDto = {} as any,
    private readonly onSave?: AccountSaveMethod,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });

    Object.assign(this, dto);

    this.labels = new Labels(labels, this.save);

    const servicesDto = Array.isArray(services) ? services : [];
    const itemsByProviderType = groupByProvider(servicesDto);
    this.storages = new Storages(
      itemsByProviderType.storage,
      dto.selectedStorage,
      this.save,
    );
    this.webhooks = new Webhooks(
      itemsByProviderType.webhook,
      dto.selectedWebhook,
      this.save,
    );
    this.cdn = new Cdn(itemsByProviderType.cdn[0], this.save);
  }

  async save(options?: AccountSaveArgs) {
    await this.onSave?.(options);
  }

  toJSON(): Required<ConfigurationsDto> {
    return {
      disablePulseKeys: this.disablePulseKeys,
      isWebhookEnabled: this.isWebhookEnabled,
      labels: this.labels.toJSON(),
      lowSerialThreshold: this.lowSerialThreshold,
      managedStorage: this.managedStorage,
      orderEmailTemplate: this.orderEmailTemplate,
      pricePercentagePerKey: this.pricePercentagePerKey,
      selectedStorage: this.storages.selectedProvider as any,
      selectedWebhook: this.webhooks.selectedProvider as any,
      skipWebhookOrders: this.skipWebhookOrders,
      services: [
        ...this.storages.toJSON(),
        ...this.webhooks.toJSON(),
        ...(this.cdn.isEmpty ? [] : [this.cdn.toJSON()]),
      ],
    };
  }

  handleChange(e: EventOrValue) {
    const { name, value } = extractValue(e);
    Object.assign(this, { [name]: value });
  }

  handleChangeAndSave(e: EventOrValue) {
    this.handleChange(e);
    this.save().catch(errorHandler('Cannot change account configuration'));
  }
}

export interface ConfigurationsDto {
  disablePulseKeys?: boolean;
  isWebhookEnabled?: boolean;
  labels?: string[];
  lowSerialThreshold?: number;
  managedStorage?: boolean;
  orderEmailTemplate?: string;
  pricePercentagePerKey?: number;
  selectedStorage?: StorageProvider;
  selectedWebhook?: WebhookProvider;
  services?: ServicesDto;
  skipWebhookOrders?: boolean;
}
